import { Directive, ElementRef, Input, Renderer2, HostListener } from '@angular/core';

/**
 * `ShortenWithoutHoverDirective` is a directive that shortens the text
 * content of an HTML element. The full text is only displayed on mouse hover.
 */
@Directive({
  selector: '[appShortenWithoutHover]',
})
export class ShortenWithoutHoverDirective {
  private shortenedText: string; // displayed when not hovered
  private hiddenText: string; // revealed upon hover
  private originalText: string; // original full text content
  @Input() expansionDirection: 'vertical' | 'horizontal' = 'vertical';
  @Input() maxLength: number;

  constructor(private el: ElementRef, private renderer: Renderer2) {}

  // Setter to allow text content updates from parent components
  @Input() set text(value: string | number) {
    if (value && value !== this.originalText) {
      this.shortenText(value.toString());
    }
  }

  @Input() set appShortenWithoutHover(value: number) {
    if (value !== this.maxLength) {
      this.maxLength = value;
      if (this.originalText) {
        this.shortenText(this.originalText);
      }
    }
  }

  // On mouse hover, display the full text based on the direction specified.
  @HostListener('mouseenter') onMouseEnter(): void {
    if (!this.originalText) {
      return;
    }
    const replaceValue = this.expansionDirection === 'vertical' ? '\n' : '';
    this.updateTextInView(this.shortenedText.replace('...', replaceValue) + this.hiddenText);
  }

  // Display the shortened text based on the `maxLength` provided.
  @HostListener('mouseleave') onMouseLeave(): void {
    if (!this.originalText) {
      return;
    }
    this.updateTextInView(this.shortenedText);
  }

  /**
   * Determines how the text should be shortened based on the `maxLength` provided
   * and ensures that words are not split.
   */
  private shortenText(value: string): void {
    this.originalText = value;
    if (!this.originalText || !this.maxLength) {
      return;
    }
    if (this.originalText.length > this.maxLength) {
      const lastSpaceIndex = this.originalText.lastIndexOf(' ', this.maxLength);
      this.shortenedText = this.originalText.substring(0, lastSpaceIndex) + '...';
      this.hiddenText = this.originalText.substring(lastSpaceIndex).trim();
      if (this.expansionDirection === 'vertical') {
        this.hiddenText = this.breakTextIntoLines(this.hiddenText);
      }
    } else {
      this.shortenedText = this.originalText;
      this.hiddenText = '';
    }

    this.updateTextInView(this.shortenedText);
  }

  private updateTextInView(text: string): void {
    this.renderer.setProperty(this.el.nativeElement, 'innerText', text);
  }

  /**
   * Helps to split the text into multiple lines so that it doesn't exceed the `maxLength`.
   */
  private breakTextIntoLines(text: string): string {
    let result = '';
    let line = '';

    text.split(' ').forEach((word) => {
      if ((line + word).length <= this.maxLength) {
        line += word + ' ';
      } else {
        result += line.trim() + '\n';
        line = word + ' ';
      }
    });

    result += line.trim();
    return result;
  }
}
